import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Typography,
  IconButton,
  Box,
  Chip,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';

const CreateSupplierModal = ({ open, onClose, onSubmit, loading = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    supplier_code: '',
    company_name: '',
    vat_number: '',
    fiscal_code: '',
    address: '',
    city: '',
    postal_code: '',
    province: '',
    phone: '',
    mobile: '',
    email: '',
    pec: '',
    sdi_code: '',
    contact_person: '',
    payment_terms: '30 giorni data fattura',
    payment_days: 30,
    iban: '',
    bank_name: '',
    contract_start_date: null,
    contract_end_date: null,
    current_price_per_liter: '',
    notes: '',
    status: 'active'
  });

  const [planners, setPlanners] = useState([]);

  useEffect(() => {
    if (!open) {
      setFormData({
        supplier_code: '',
        company_name: '',
        vat_number: '',
        fiscal_code: '',
        address: '',
        city: '',
        postal_code: '',
        province: '',
        phone: '',
        mobile: '',
        email: '',
        pec: '',
        sdi_code: '',
        contact_person: '',
        payment_terms: '30 giorni data fattura',
        payment_days: 30,
        iban: '',
        bank_name: '',
        contract_start_date: null,
        contract_end_date: null,
        current_price_per_liter: '',
        notes: '',
        status: 'active'
      });
      setPlanners([]);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addPlanner = () => {
    setPlanners(prev => [...prev, {
      name: '',
      email: '',
      phone: '',
      mobile: '',
      role: 'Pianificatore',
      coverage_provinces: [],
      is_primary: prev.length === 0,
      is_active: true
    }]);
  };

  const updatePlanner = (index, field, value) => {
    setPlanners(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removePlanner = (index) => {
    setPlanners(prev => prev.filter((_, i) => i !== index));
  };

  const handleProvincesChange = (index, value) => {
    // Converte stringa in array (es: "NA, CE, AV" -> ["NA", "CE", "AV"])
    const provinces = value.split(',').map(p => p.trim().toUpperCase()).filter(p => p);
    updatePlanner(index, 'coverage_provinces', provinces);
  };

  const handleSubmit = () => {
    if (!formData.supplier_code || !formData.company_name) {
      return;
    }

    const formatDate = (date) => date ? date.toISOString().split('T')[0] : null;

    onSubmit({
      ...formData,
      contract_start_date: formatDate(formData.contract_start_date),
      contract_end_date: formatDate(formData.contract_end_date),
      planners
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle>Nuovo Fornitore GPL</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Dati principali */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Dati Azienda
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Codice Fornitore *"
                name="supplier_code"
                value={formData.supplier_code}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ragione Sociale *"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Stato</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Stato"
                >
                  <MenuItem value="active">Attivo</MenuItem>
                  <MenuItem value="inactive">Inattivo</MenuItem>
                  <MenuItem value="suspended">Sospeso</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Partita IVA"
                name="vat_number"
                value={formData.vat_number}
                onChange={handleChange}
                placeholder="IT01234567890"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Codice Fiscale"
                name="fiscal_code"
                value={formData.fiscal_code}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Referente"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
              />
            </Grid>

            {/* Indirizzo */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Indirizzo
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Indirizzo"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Città"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <TextField
                fullWidth
                label="CAP"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Provincia"
                name="province"
                value={formData.province}
                onChange={handleChange}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>

            {/* Contatti */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Contatti
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Telefono"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Cellulare"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="PEC"
                name="pec"
                type="email"
                value={formData.pec}
                onChange={handleChange}
              />
            </Grid>

            {/* Fatturazione */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Dati Fatturazione
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Codice SDI"
                name="sdi_code"
                value={formData.sdi_code}
                onChange={handleChange}
                inputProps={{ maxLength: 7 }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Termini Pagamento"
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="IBAN"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Banca"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
              />
            </Grid>

            {/* Contratto */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Contratto GPL
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePicker
                label="Inizio Contratto"
                value={formData.contract_start_date}
                onChange={(date) => setFormData(prev => ({ ...prev, contract_start_date: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePicker
                label="Fine Contratto"
                value={formData.contract_end_date}
                onChange={(date) => setFormData(prev => ({ ...prev, contract_end_date: date }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Prezzo corrente GPL"
                name="current_price_per_liter"
                type="number"
                value={formData.current_price_per_liter}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  endAdornment: <InputAdornment position="end">/L</InputAdornment>
                }}
                inputProps={{ step: 0.001 }}
              />
            </Grid>

            {/* Pianificatori */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Pianificatori
                </Typography>
                <Button startIcon={<AddIcon />} onClick={addPlanner} size="small">
                  Aggiungi Pianificatore
                </Button>
              </Box>
            </Grid>

            {planners.map((planner, index) => (
              <Grid item xs={12} key={index}>
                <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Nome *"
                        value={planner.name}
                        onChange={(e) => updatePlanner(index, 'name', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={planner.email}
                        onChange={(e) => updatePlanner(index, 'email', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Telefono"
                        value={planner.phone}
                        onChange={(e) => updatePlanner(index, 'phone', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Province (es: NA, CE, AV)"
                        value={planner.coverage_provinces?.join(', ') || ''}
                        onChange={(e) => handleProvincesChange(index, e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={planner.is_primary}
                              onChange={(e) => updatePlanner(index, 'is_primary', e.target.checked)}
                              size="small"
                            />
                          }
                          label="Principale"
                          sx={{ mr: 0 }}
                        />
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => removePlanner(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}

            {/* Note */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <TextField
                fullWidth
                label="Note"
                name="notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annulla
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.supplier_code || !formData.company_name}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Crea Fornitore
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSupplierModal;
